/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { style } from "style";
import { Creators as AcademyActions } from "store/ducks/academy";

import Portal from "templates/Portal";
import Flex from "components/core/Grid/Flex";
import Button from "components/core/Button";
import { history, manageClasses } from "helpers/sharedHelpers";
import urls from "utils/constants/urls";
import ContentModule from "./ContentModule";
import ContentModuleSkeleton from "./ContentModule/ContentModuleSkeleton";
import CourseProgress from "../../Sidebar/CourseProgress";

import { ContainerModule, QuizzLength, SidebarModule } from "./ModuleStyle";
import { useVideoContext } from "../../../../context/VideoContexts";

const Module = () => {
  const { id } = useParams();
  const pageInCourse = useLocation();
  const alreadyFindCurrentSubmoduleRef = useRef(false);
  const pageTitle = pageInCourse?.state?.pageTitle || "";
  const { videoIsFinished, setVideoIsFinished, isVideo } = useVideoContext();
  const dispatch = useDispatch();
  const {
    course_details: courseDetails,
    submodule_details: submoduleDetails,
    doneSubmodules,
    isLoading,
    isLoadingModule,
    isLoadingFinalize,
    isModuleValidated,
  } = useSelector(({ academy }) => academy);

  const {
    title: titleCourse,
    user_status: userStatus,
    module_courses: courseModules,
  } = courseDetails;

  // pages controllers
  const [contModule, setContModule] = useState(0);
  const [contSubMod, setContSubMod] = useState(0);

  // quiz controllers
  const [contQuiz, setContQuiz] = useState(1);
  const [hasAnswer, setHasAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [idContent, setIdContent] = useState(null);
  const [idAnswer, setIdAnswer] = useState(null);

  // submodules and modules finalized
  const [idCourse, setIdCourse] = useState(null);
  const [idModule, setIdModule] = useState(null);
  const [idSubmodule, setIdSubmodule] = useState(null);
  const [finalizeSubmodule, setFinalizeSubmodule] = useState(null);
  const [finalizedModule, setFinalizedModule] = useState(null);
  const [titleModule, setTitleModule] = useState(null);

  const [isFinalizingModule, setIsFinalizingModule] = useState(false);
  const [isCourseFinished, setIsCourseFinished] = useState(false);

  // progress page controllers if quiz
  const [isQuiz, setIsQuiz] = useState(false);

  const [isFinalized, setIsFinalized] = useState(false);
  const [pageProgress, setPageProgress] = useState(true);

  useEffect(() => {
    const submoduleId =
      courseModules?.[contModule]?.submodules?.[contSubMod]?.id;

    if (submoduleId) {
      dispatch(AcademyActions.getDetailsCourseSubmodule(submoduleId));
    }
  }, [dispatch, id, contModule, contSubMod]);

  // function to select item in module
  const moduleItem = useCallback(
    (type) => {
      switch (type) {
        case "module":
          return courseModules[contModule];
        case "submodules":
          return courseModules[contModule]?.submodules;
        case "submodule":
          return courseModules?.[contModule]?.submodules?.[contSubMod];
        case "contents":
          return submoduleDetails || null;
        case "quiz":
          return submoduleDetails[0]?.kind_content === "quiz"
            ? submoduleDetails[0]
            : null;
        default:
          return submoduleDetails || null;
      }
    },
    [courseModules, contModule, contSubMod, submoduleDetails],
  );
  // end

  // function last module and submodule
  const lastSubModule = useCallback(
    (module, submodule) => {
      const filteredSubmodules = courseModules
        .map((mdlItem) =>
          mdlItem.submodules.filter((submoduleItem) => !submoduleItem.was_seen),
        )
        .flat();

      const last = filteredSubmodules.slice(-1)[0];

      const targetSubmodule = courseModules[module]?.submodules[submodule];

      setIsFinalized(targetSubmodule && targetSubmodule?.id === last?.id);
    },
    [courseModules],
  );
  // end

  const wasModuleAdded =
    userStatus?.status === "finished" &&
    courseModules?.some(({ was_finished }) => !was_finished);

  useEffect(() => {
    const finalSubmodule = courseModules
      ?.slice(-1)?.[0]
      ?.submodules?.slice(-1)?.[0];

    const allModulesCompleted = courseModules?.every(
      (mdlItem) => mdlItem.was_finished,
    );

    setIsCourseFinished(
      finalSubmodule?.id === moduleItem("submodule")?.id && allModulesCompleted,
    );
  }, [courseModules, moduleItem]);

  useEffect(() => {
    if (isFinalizingModule && isModuleValidated) {
      setIsFinalizingModule(false);
      dispatch(AcademyActions.clearModuleValidated());

      // Avança módulo aqui:
      setContSubMod(0);
      setContQuiz(1);
      setContModule((prev) => prev + 1);
    }
  }, [isFinalizingModule, isModuleValidated, dispatch]);

  // finalize modules and submodules
  useEffect(() => {
    if (finalizedModule) {
      dispatch(
        AcademyActions.finalizeModule({
          idCourse,
          idModule,
          titleModule,
          isFinalized,
          titleCourse,
        }),
      );
      setFinalizedModule(false);
    }
  }, [
    finalizedModule,
    idCourse,
    idModule,
    titleModule,
    isFinalized,
    titleCourse,
    dispatch,
  ]);

  const finalizeModule = () => {
    if (isFinalized) {
      setFinalizeSubmodule(true);
      setIdCourse(id);
      setIdSubmodule(moduleItem("submodule").id);
    }
    if (!moduleItem("module").was_finished) {
      setIdCourse(id);
      setIdModule(moduleItem("module").id);
      setTitleModule(moduleItem("module").name);
      setFinalizedModule(true);
    }
  };

  useEffect(() => {
    if (finalizeSubmodule) {
      dispatch(AcademyActions.finalizeSubmodule({ idCourse, idSubmodule }));
      setFinalizeSubmodule(false);
    }
  }, [finalizeSubmodule, idCourse, idSubmodule, titleModule, dispatch]);
  // end

  // page in progress
  useEffect(() => {
    if (isLoading) return;

    if (pageProgress) {
      if (
        courseModules.length === 1 &&
        courseModules[0].submodules.length === 1
      ) {
        setIsFinalized(true);
      }

      if (pageInCourse.state?.module) {
        if (alreadyFindCurrentSubmoduleRef.current) return;

        setContModule(pageInCourse.state.module);
        setContSubMod(pageInCourse.state.submodule);
        alreadyFindCurrentSubmoduleRef.current = true;
      } else if (userStatus?.status === "initiated" || wasModuleAdded) {
        if (alreadyFindCurrentSubmoduleRef.current || !courseModules) return;

        const moduleIndex = courseModules.findIndex(
          ({ was_finished }) => !was_finished,
        );
        const submodIndex = courseModules[moduleIndex]?.submodules.findIndex(
          ({ was_seen }) => !was_seen,
        );

        lastSubModule(moduleIndex, submodIndex);
        setContModule(moduleIndex);
        setContSubMod(submodIndex);

        alreadyFindCurrentSubmoduleRef.current = true;

        if (!moduleItem("quiz")) return;
        setContQuiz(submodIndex > 0 ? moduleItem("quiz")?.question_number : 1);
      }
    }
  }, [
    courseModules,
    isLoading,
    pageProgress,
    pageInCourse.state,
    lastSubModule,
    userStatus?.status,
    moduleItem,
    submoduleDetails,
  ]);
  // end

  // Back and next function
  const backSlide = () => {
    const hasBack = !!courseModules[contModule]?.submodules[contSubMod - 1];
    lastSubModule(contModule, contSubMod - 1);

    if (contSubMod === 0)
      return history.push(`${urls.ROUTES.ACADEMY}/${id}`, { pageTitle });

    if (hasBack) {
      if (submoduleDetails[0]?.kind_content === "quiz") {
        setContQuiz(submoduleDetails[0]?.question_number);
      }

      setContSubMod(contSubMod - 1);
    }
  };

  const nextSlide = () => {
    setVideoIsFinished(false);

    const hasNext = (modules) => !!modules[contModule + 1];
    const lastSubmod = (submodule) => submodule?.length - 1 === contSubMod;

    const finalizedSubmod =
      moduleItem("submodule")?.was_seen ||
      !!doneSubmodules?.find(
        (doneSubmodule) => doneSubmodule === moduleItem("submodule").id,
      );

    setPageProgress(false);
    setHasAnswer(null);
    setIsCorrect(null);

    if (!finalizedSubmod) {
      setFinalizeSubmodule(true);
      setIdCourse(id);
      setIdSubmodule(moduleItem("submodule")?.id);
    }

    lastSubModule(contModule, contSubMod + 1);

    const isLastSubmod = lastSubmod(moduleItem("submodules"));
    const hasNextModule = hasNext(courseModules);

    if (isLastSubmod && hasNextModule) {
      finalizeModule();
      setIsFinalizingModule(true);
    } else if (!isLastSubmod) {
      const nextSubmodule =
        courseModules[contModule]?.submodules[contSubMod + 1];
      if (nextSubmodule) {
        setContSubMod(contSubMod + 1);

        if (submoduleDetails[0]?.kind_content === "quiz") {
          setContQuiz(submoduleDetails[0]?.question_number);
        }
      }
    }
  };
  // end

  // Quiz controler function
  const verifyAnswer = (idValue) => {
    const correctAnswer = moduleItem("quiz")?.answers.find(
      (answer) => answer.is_correct,
    );
    lastSubModule(contModule, contSubMod);
    if (correctAnswer) {
      setIsCorrect(idValue === correctAnswer.id);
    }
    setIdCourse(id);
    setIdAnswer(idValue);
    setIdModule(moduleItem("module").id);
    setIdContent(moduleItem("quiz").id);
  };

  useEffect(() => {
    if (hasAnswer) {
      dispatch(
        AcademyActions.postAnswer({ idCourse, idModule, idContent, idAnswer }),
      );
    }
  }, [hasAnswer, dispatch, idCourse, idModule, idContent, idAnswer]);
  // end

  // page controller of course progress
  useEffect(() => {
    if (isQuiz) {
      setContQuiz(submoduleDetails[0]?.question_number);
      setIsQuiz(false);
    }
  }, [isQuiz, submoduleDetails, moduleItem]);

  const selectedPage = (modulePage, submodPage) => {
    setIsFinalized(null);
    setContModule(modulePage);
    setContSubMod(submodPage);

    setIsQuiz(!!moduleItem("quiz"));
  };

  // end
  return (
    <Portal fullHeight>
      <Flex>
        <SidebarModule>
          <CourseProgress
            pageView={(modulePage, submodPage) =>
              selectedPage(modulePage, submodPage)
            }
          />
        </SidebarModule>
        {isLoading ? (
          <ContainerModule>
            <ContentModuleSkeleton />
          </ContainerModule>
        ) : (
          <ContainerModule>
            {submoduleDetails[0]?.kind_content === "quiz" && (
              <QuizzLength>
                Pergunta {contQuiz} de {moduleItem("module")?.quizzes_amount}
              </QuizzLength>
            )}

            <ContentModule
              titleCourse={titleCourse}
              titleSubmodule={
                courseModules[contModule]?.submodules[contSubMod]?.name
              }
              contents={submoduleDetails}
              answer={(selectOption) => setHasAnswer(selectOption)}
              isCorrect={isCorrect}
            />
            <Flex mt={4} justifyContent="flex-end" spaceChildren={3}>
              <Button
                name="Voltar"
                variant={manageClasses(
                  userStatus?.status === "finished" && !wasModuleAdded
                    ? "completedTransparent"
                    : "inProgressTransparent",
                )}
                action={backSlide}
              />
              {submoduleDetails[0]?.kind_content === "quiz" &&
              !submoduleDetails[0]?.answers?.find((answer) => answer.chosen) &&
              isCorrect === null ? (
                <Button
                  name="Responder"
                  action={() => verifyAnswer(hasAnswer)}
                  variant={manageClasses("inProgress", {
                    disabled: !hasAnswer,
                  })}
                />
              ) : isFinalized ? (
                pageInCourse.state?.module ? (
                  <Button
                    name={
                      style.variables.child_profiles?.length > 0
                        ? "visualizar"
                        : "Ver Cursos"
                    }
                    variant={manageClasses(
                      userStatus?.status === "finished" && !wasModuleAdded
                        ? "completed"
                        : "inProgress",
                    )}
                    action={() =>
                      history.push(`${urls.ROUTES.ACADEMY}`, { pageTitle })
                    }
                  />
                ) : (
                  <Button
                    name="Finalizar"
                    variant={manageClasses(
                      userStatus?.status === "finished" && !wasModuleAdded
                        ? "completed"
                        : "inProgress",
                    )}
                    action={finalizeModule}
                    isLoading={isLoadingModule || isLoadingFinalize}
                    disabled={
                      (!videoIsFinished &&
                        isVideo &&
                        (style.names.nameApp === "Pintou Parceria Suvinil" ||
                          style.names.nameApp === "ClubCasa" ||
                          style.names.nameApp === "NOZ" ||
                          style.names.nameApp === "somosmachado" ||
                          style.names.nameApp === "Serquip mg" ||
                          style.names.nameApp === "sample" ||
                          style.names.nameApp === "astro")) ||
                      isLoadingModule ||
                      isLoadingFinalize
                    }
                  />
                )
              ) : (
                !isCourseFinished && (
                  <Button
                    name="Próximo"
                    variant={manageClasses(
                      userStatus?.status === "finished" && !wasModuleAdded
                        ? "completed"
                        : "inProgress",
                    )}
                    action={nextSlide}
                    isLoading={
                      isFinalizingModule || isLoadingModule || isLoadingFinalize
                    }
                    disabled={
                      ((userStatus?.status !== "finished" || !wasModuleAdded) &&
                        !videoIsFinished &&
                        isVideo &&
                        (style.names.nameApp === "Pintou Parceria Suvinil" ||
                          style.names.nameApp === "ClubCasa" ||
                          style.names.nameApp === "somosmachado" ||
                          style.names.nameApp === "Serquip mg" ||
                          style.names.nameApp === "sample" ||
                          style.names.nameApp === "astro")) ||
                      isLoadingModule ||
                      isLoadingFinalize ||
                      isFinalizingModule
                    }
                  />
                )
              )}
            </Flex>
          </ContainerModule>
        )}
      </Flex>
    </Portal>
  );
};

export default Module;
